/**
 * Човен: a flat-bottomed river boat, out of five boxes.
 *
 * A довбанка - the dugout that every river in this country has had on it -
 * rather than a Viking longship or a rowing boat with ribs. Which at this
 * scale means one thing: it is a *trough*. Flat bottom, two sides, two ends,
 * and the ends a little higher than the sides so the silhouette rises at bow
 * and stern the way a real one does. Nothing inside it, because a player is
 * about to be standing there.
 *
 * Unlit, like everything else in this scene - see scripts/check-lighting.mjs,
 * which exists because the animals shipped rendering black once already.
 */

import * as THREE from "three";

/** Outside measurements, in blocks. Two blocks long and one across. */
const LENGTH = 2.1;
const BEAM = 0.94;
const FREEBOARD = 0.42;
const PLANK = 0.1;
/** How much higher the bow and stern stand than the sides. */
const END_RISE = 1.9;

/** Oak, and the inside a shade darker because it is in its own shadow. */
const HULL = 0x9a6f42;
const INSIDE = 0x6f4e2c;

interface BoatGpu {
  readonly floor: THREE.BufferGeometry;
  readonly side: THREE.BufferGeometry;
  readonly end: THREE.BufferGeometry;
  readonly hull: THREE.MeshBasicMaterial;
  readonly inside: THREE.MeshBasicMaterial;
  readonly bases: readonly [THREE.Color, THREE.Color];
}
let shared: BoatGpu | null = null;

function boatGpu(): BoatGpu {
  if (shared !== null) return shared;
  shared = {
    floor: new THREE.BoxGeometry(BEAM, PLANK, LENGTH),
    side: new THREE.BoxGeometry(PLANK, FREEBOARD, LENGTH),
    end: new THREE.BoxGeometry(BEAM, FREEBOARD * END_RISE, PLANK),
    hull: new THREE.MeshBasicMaterial({ color: HULL }),
    inside: new THREE.MeshBasicMaterial({ color: INSIDE }),
    bases: [new THREE.Color(HULL), new THREE.Color(INSIDE)],
  };
  return shared;
}

/** Every boat darkens together with the day, because every boat is one boat. */
export function tintBoats(color: THREE.Color): void {
  const gpu = shared;
  if (gpu === null) return;
  gpu.hull.color.copy(gpu.bases[0]).multiply(color);
  gpu.inside.color.copy(gpu.bases[1]).multiply(color);
}

export class BoatModel {
  readonly group = new THREE.Group();

  constructor() {
    const gpu = boatGpu();

    /*
     * The origin is the top of the floor, not the middle of the hull.
     *
     * Which is where the person standing in it has their feet - so the boat
     * can be put at the player's position and the two agree without anybody
     * having to remember an offset. The first one was built round its own
     * middle and the passenger stood in it up to the waist.
     */
    const floor = new THREE.Mesh(gpu.floor, gpu.inside);
    floor.position.y = -PLANK / 2;
    this.group.add(floor);

    for (const sign of [1, -1]) {
      const side = new THREE.Mesh(gpu.side, gpu.hull);
      side.position.set(sign * (BEAM / 2 - PLANK / 2), FREEBOARD / 2 - PLANK, 0);
      this.group.add(side);

      // Bow and stern stand well above the sides. That rise is the whole
      // silhouette of a river boat seen from the bank, and the first version
      // had it at a tenth of the freeboard, where it read as a tray.
      const end = new THREE.Mesh(gpu.end, gpu.hull);
      end.position.set(
        0, (FREEBOARD * END_RISE) / 2 - PLANK, sign * (LENGTH / 2 - PLANK / 2),
      );
      this.group.add(end);
    }
  }

  /**
   * Put it where it is and point it.
   *
   * The hull is built along Z, and `yaw` is the same yaw the player and the
   * arrows use - so a boat and the person in it face the same way for the
   * same number, and there is no second convention to get backwards.
   */
  setPose(x: number, y: number, z: number, yaw: number): void {
    this.group.position.set(x, y, z);
    this.group.rotation.y = yaw;
  }

  /** Nothing to give back: the geometry and the materials are the boat's. */
  dispose(): void {
    // Intentionally empty: see boatGpu.
  }
}
